// src/modules/units/tyres/shared/retread-batch-vocab.js
//
// One place that turns every retread-batch code the backend can send — tyre
// eligibility reasons, warnings, receive outcomes, batch/line/operation states,
// and the recovery decision — into the words and the tone the UI shows. Same
// reason tyre-status.js centralises status labels: the vocabulary is defined once,
// so two screens can never disagree, and a new backend code needs editing in
// exactly one file.
//
// Pure and import-light (only the core enums), so the Node harness covers it: a
// completeness test asserts every enum value here resolves to copy, which is what
// stops a silently-unlabelled code reaching a user.
//
// `tone` names map to the --tm-tone-* tokens already in tyres.css (see STATUS_META
// in tyre-status.js): stock, fitted, fit, retread, slate, red, amber.

import {
  RETREAD_TYRE_ERROR, RETREAD_WARNING, RETREAD_OUTCOME,
  RETREAD_BATCH_STATUS, RETREAD_LINE_STATUS, OPERATION_STATE, RECOVERY
} from './retread-batch-core.js';

// ---- per-tyre eligibility reasons (tyreErrors[].reason) --------------------
// A short, plain statement about THAT tyre, so the picker / receive form can show
// it inline next to the offending id.
const TYRE_ERROR_COPY = Object.freeze({
  [RETREAD_TYRE_ERROR.TYRE_NOT_FOUND]: 'Not found for this customer.',
  [RETREAD_TYRE_ERROR.TYRE_NOT_IN_STOCK]: 'Not in stock, so it can’t be sent.',
  [RETREAD_TYRE_ERROR.TYRE_FITTED]: 'Still fitted — remove it from its sensor first.',
  [RETREAD_TYRE_ERROR.TYRE_ON_OTHER_BATCH]: 'Already on another retread batch.',
  [RETREAD_TYRE_ERROR.TYRE_ALREADY_OUT]: 'Already out at the retreader.',
  [RETREAD_TYRE_ERROR.TYRE_DUPLICATE]: 'Listed more than once — remove the duplicate.',
  [RETREAD_TYRE_ERROR.TYRE_NOT_OUT]: 'Not out on a batch, so it can’t be received.',
  [RETREAD_TYRE_ERROR.TYRE_NOT_ON_THIS_BATCH]: 'Not part of this batch.',
  [RETREAD_TYRE_ERROR.TYRE_WRONG_SUPPLIER]: 'Out with a different supplier.',
  [RETREAD_TYRE_ERROR.REJECT_REASON_REQUIRED]: 'Needs a reason before it can be rejected or written off.'
});

/** Human line for a per-tyre eligibility reason; generic fallback for unknowns. */
export function tyreErrorText(code) {
  return TYRE_ERROR_COPY[code] || 'This tyre can’t be used here.';
}

// ---- warnings (warnings[].code) — non-blocking ----------------------------
const WARNING_COPY = Object.freeze({
  [RETREAD_WARNING.RETREAD_CAP_REACHED]:
    'At or past its retread limit — sending it again is allowed, but check the casing.'
});

// warnings[] also carries operational notes (e.g. STANDALONE_MONGODB_NON_ATOMIC)
// that are not actionable by the user, so an unknown code falls back to the
// server's own message where present, then to a neutral line.
export function warningText(warning) {
  if (!warning) return '';
  return WARNING_COPY[warning.code] || warning.message || 'Completed with a warning.';
}

// ---- receive outcome ------------------------------------------------------
const OUTCOME_META = Object.freeze({
  [RETREAD_OUTCOME.RETREADED]: { label: 'Retreaded', tone: 'fit', short: 'Back in stock, retreaded.' },
  [RETREAD_OUTCOME.REJECTED]: { label: 'Rejected', tone: 'red', short: 'Casing failed — scrapped.' },
  [RETREAD_OUTCOME.WRITTEN_OFF]: { label: 'Written off', tone: 'slate', short: 'Written off.' }
});
export function outcomeMeta(outcome) {
  return OUTCOME_META[outcome] || { label: outcome || 'Outcome', tone: 'slate', short: '' };
}

// ---- batch status ---------------------------------------------------------
const BATCH_STATUS_META = Object.freeze({
  [RETREAD_BATCH_STATUS.DRAFT]: { label: 'Draft', tone: 'slate' },
  [RETREAD_BATCH_STATUS.OPEN]: { label: 'Out at retread', tone: 'retread' },
  [RETREAD_BATCH_STATUS.PARTIALLY_RETURNED]: { label: 'Part returned', tone: 'amber' },
  [RETREAD_BATCH_STATUS.CLOSED]: { label: 'Closed', tone: 'primary-blue' },
  [RETREAD_BATCH_STATUS.CANCELLED]: { label: 'Cancelled', tone: 'slate' }
});
export function batchStatusMeta(status) {
  return BATCH_STATUS_META[status] || { label: status || 'Unknown', tone: 'slate' };
}

// ---- line status ----------------------------------------------------------
const LINE_STATUS_META = Object.freeze({
  [RETREAD_LINE_STATUS.DRAFT]: { label: 'Draft', tone: 'slate' },
  [RETREAD_LINE_STATUS.OUT]: { label: 'Out', tone: 'retread' },
  [RETREAD_LINE_STATUS.RETURNED_RETREADED]: { label: 'Retreaded', tone: 'fit' },
  [RETREAD_LINE_STATUS.RETURNED_REJECTED]: { label: 'Rejected', tone: 'red' },
  [RETREAD_LINE_STATUS.WRITTEN_OFF]: { label: 'Written off', tone: 'slate' },
  [RETREAD_LINE_STATUS.CANCELLED]: { label: 'Cancelled', tone: 'slate' }
});
export function lineStatusMeta(status) {
  return LINE_STATUS_META[status] || { label: status || 'Unknown', tone: 'slate' };
}

// ---- operation state (the saga surface) -----------------------------------
// IDLE is intentionally blank — a settled batch shows no operation chip at all.
const OPERATION_STATE_META = Object.freeze({
  [OPERATION_STATE.IDLE]: { label: '', tone: 'slate' },
  [OPERATION_STATE.IN_PROGRESS]: { label: 'Working…', tone: 'amber' },
  [OPERATION_STATE.FAILED]: { label: 'Needs attention', tone: 'red' },
  [OPERATION_STATE.ROLLING_BACK]: { label: 'Undoing…', tone: 'amber' },
  [OPERATION_STATE.ROLLED_BACK]: { label: 'Rolled back', tone: 'slate' },
  [OPERATION_STATE.ROLLBACK_FAILED]: { label: 'Stuck', tone: 'red' }
});
export function operationStateMeta(state) {
  return OPERATION_STATE_META[state] || { label: '', tone: 'slate' };
}

// ---- recovery (what to DO about a failure) --------------------------------
//   label       : short banner heading
//   instruction : one sentence — what will happen / what to do
//   actionLabel : primary button text, or null when there is no user action
// NONE is intentionally blank — success, or an eligibility problem shown elsewhere.
const RECOVERY_META = Object.freeze({
  [RECOVERY.RETRY]: {
    label: 'Didn’t finish',
    tone: 'amber',
    instruction: 'The last step didn’t complete. Retry it with the same details — it resumes where it stopped and won’t double up.',
    actionLabel: 'Retry'
  },
  [RECOVERY.WAIT]: {
    label: 'Busy',
    tone: 'amber',
    instruction: 'Another operation on this batch is still finishing. Give it a moment, then refresh.',
    actionLabel: 'Refresh'
  },
  [RECOVERY.ESCALATE]: {
    label: 'Needs an administrator',
    tone: 'red',
    instruction: 'This couldn’t be recovered automatically. An administrator may need to roll the batch back to its previous state.',
    actionLabel: null
  },
  [RECOVERY.NONE]: { label: '', tone: 'slate', instruction: '', actionLabel: null }
});
export function recoveryMeta(recovery) {
  return RECOVERY_META[recovery] || RECOVERY_META[RECOVERY.NONE];
}
